import React, { type FormEvent, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';

import axios, { type AxiosResponse } from 'axios';
import { toast } from 'sonner';

import { Breadcrumb, Button, Panel } from '@shared/components/bootstrap';
import { useURLSearchParam } from '@shared/hooks/use_url_search_param';

import { Spinner } from '@admin/components/spinner';
import { useAxiosRequest } from '@admin/hooks/use_axios_request';
import { useTitle } from '@admin/hooks/use_title';

type SystemPhone = {
  id: number;
  number: string;
  campaign_id?: number | null;
};

type Campaign = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
};

const emptyCampaign = (): Campaign => ({
  utm_source: '',
  utm_medium: '',
  utm_campaign: '',
  utm_term: '',
  utm_content: '',
});

const LOAD_ERROR_MESSAGE = 'Unable to load system phones. Please try again.';
const SAVE_ERROR_MESSAGE = 'Unable to save campaign. Please try again.';

const useSystemPhonesRequest = () => {
  const [request, setRequest] = useState<Promise<AxiosResponse<SystemPhone[]>> | undefined>();

  useEffect(() => {
    setRequest(axios.get<SystemPhone[]>('/system_phones.json'));
  }, []);

  const reload = () => {
    setRequest(axios.get<SystemPhone[]>('/system_phones.json'));
  };

  return { reload, ...useAxiosRequest(request) };
};

const useCampaignRequest = (campaignId?: number) => {
  const [request, setRequest] = useState<Promise<AxiosResponse<Campaign>> | undefined>();

  useEffect(() => {
    if (!campaignId) {
      setRequest(undefined);
      return;
    }

    setRequest(axios.get<Campaign>(`/marketing/campaigns/${campaignId}.json`));
  }, [campaignId]);

  return useAxiosRequest(request);
};

export const UtmPhoneNew: React.FC = () => {
  useTitle('Add Campaign to Phone');
  const { loading, response: phonesResponse, error: phonesError, reload } = useSystemPhonesRequest();
  const systemPhones = phonesResponse?.data ?? [];
  const [phoneId, setPhoneId] = useState<number | undefined>();
  const [campaign, setCampaign] = useState<Campaign>(emptyCampaign());
  const [saving, setSaving] = useState(false);

  const selectedPhone = systemPhones.find((phone) => phone.id === phoneId);
  const {
    loading: campaignLoading,
    response: campaignResponse,
    error: campaignError,
  } = useCampaignRequest(selectedPhone?.campaign_id ?? undefined);

  useEffect(() => {
    if (!selectedPhone?.campaign_id) {
      setCampaign(emptyCampaign());
      return;
    }

    if (campaignError) {
      setCampaign(emptyCampaign());
      return;
    }

    if (!campaignResponse?.data) return;

    const data = campaignResponse.data;
    setCampaign({
      utm_source: data.utm_source ?? '',
      utm_medium: data.utm_medium ?? '',
      utm_campaign: data.utm_campaign ?? '',
      utm_term: data.utm_term ?? '',
      utm_content: data.utm_content ?? '',
    });
  }, [selectedPhone?.campaign_id, campaignResponse, campaignError]);

  const onPhoneChange = (nextPhoneId: number | undefined) => {
    const nextPhone = systemPhones.find((phone) => phone.id === nextPhoneId);
    const nextCampaignId = nextPhone?.campaign_id ?? undefined;
    const currentCampaignId = selectedPhone?.campaign_id ?? undefined;

    setPhoneId(nextPhoneId);

    if (nextCampaignId !== currentCampaignId) {
      setCampaign(emptyCampaign());
    }
  };
  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedPhone) return;

    setSaving(true);
    try {
      await axios.post('/marketing/utm_phones.json', {
        phoneId: selectedPhone.id,
        ...campaign,
      });
      window.location.assign('/system_phones');
    } catch (error) {
      const message = axios.isAxiosError(error) ? (error.response?.data as { message?: string })?.message : undefined;
      toast.error(message ?? SAVE_ERROR_MESSAGE);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Spinner />;
  }

  if (phonesError) {
    return (
      <div className="alert alert-danger" role="alert">
        {LOAD_ERROR_MESSAGE}{' '}
        <button type="button" className="btn btn-default" onClick={reload}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <h1 className="page-header text-overflow">
        <Breadcrumb>
          <Breadcrumb.Item>
            <NavLink to="/system_phones">System Phone</NavLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item active>Add Campaign</Breadcrumb.Item>
        </Breadcrumb>
      </h1>

      <form className="form-horizontal" role="form" onSubmit={(event) => void onSubmit(event)}>
        <div className="row">
          <div className="col-sm-6">
            <Panel>
              <Panel.Header>
                <Panel.Title>Phone Number</Panel.Title>
              </Panel.Header>
              <div className="form-group panel-body">
                <label className="col-sm-3 control-label" htmlFor="utm-phone-select">
                  Phone Number
                </label>
                <div className="col-sm-3">
                  <select
                    id="utm-phone-select"
                    className="form-control"
                    value={phoneId ?? ''}
                    onChange={(event) => onPhoneChange(event.target.value ? Number(event.target.value) : undefined)}
                  >
                    <option value="">System Phone</option>
                    {systemPhones.map((phone) => (
                      <option key={phone.id} value={phone.id}>
                        {phone.number}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </Panel>
          </div>

          <div className="col-sm-6">
            <Panel>
              <Panel.Header>
                <Panel.Title>Campaign</Panel.Title>
              </Panel.Header>
              <Panel.Body>
                <div className="form-group">
                  <label className="col-sm-3 control-label" htmlFor="utm-source">
                    Utm Source
                  </label>
                  <div className="col-sm-3">
                    <input
                      id="utm-source"
                      className="form-control"
                      value={campaign.utm_source ?? ''}
                      onChange={(event) => setCampaign((current) => ({ ...current, utm_source: event.target.value }))}
                      required
                      disabled={campaignLoading}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="col-sm-3 control-label" htmlFor="utm-medium">
                    Utm Medium
                  </label>
                  <div className="col-sm-3">
                    <input
                      id="utm-medium"
                      className="form-control"
                      value={campaign.utm_medium ?? ''}
                      onChange={(event) => setCampaign((current) => ({ ...current, utm_medium: event.target.value }))}
                      required
                      disabled={campaignLoading}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="col-sm-3 control-label" htmlFor="utm-campaign">
                    Utm Campaign
                  </label>
                  <div className="col-sm-3">
                    <input
                      id="utm-campaign"
                      className="form-control"
                      value={campaign.utm_campaign ?? ''}
                      onChange={(event) => setCampaign((current) => ({ ...current, utm_campaign: event.target.value }))}
                      required
                      disabled={campaignLoading}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="col-sm-3 control-label" htmlFor="utm-term">
                    Utm Term
                  </label>
                  <div className="col-sm-3">
                    <input
                      id="utm-term"
                      className="form-control"
                      value={campaign.utm_term ?? ''}
                      onChange={(event) => setCampaign((current) => ({ ...current, utm_term: event.target.value }))}
                      disabled={campaignLoading}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="col-sm-3 control-label" htmlFor="utm-content">
                    Utm Content
                  </label>
                  <div className="col-sm-3">
                    <input
                      id="utm-content"
                      className="form-control"
                      value={campaign.utm_content ?? ''}
                      onChange={(event) => setCampaign((current) => ({ ...current, utm_content: event.target.value }))}
                      disabled={campaignLoading}
                    />
                  </div>
                </div>
              </Panel.Body>
              <Panel.Footer align="right">
                <a href="/system_phones" className="btn">
                  Cancel
                </a>
                <Button kind="primary" type="submit" disabled={saving || campaignLoading || !selectedPhone}>
                  Save
                </Button>
              </Panel.Footer>
            </Panel>
          </div>
        </div>
      </form>
    </>
  );
};
